import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import {
  DsChipComponent,
  IconComponent,
  ItemComponent,
  ItemLeadingComponent,
  ItemTitleComponent,
  ItemTrailingComponent,
  SoftSlotDirective,
  SwitchComponent,
  TranslatePipe,
  translator
} from '@softline/ui-core';
import { PageFilterItem } from '../../../../types/page-filter-item';
import { Filter } from '../../../../../types/filter';
import { FilterOperatorComponent } from '../../../../../components/filter-operator/filter-operator.component';
import { formatFilterValueSummary, showsOperatorGlyph, summarizeFilterValue } from '../../../../../helper/format-filter-value.function';

@Component({
  selector: 'soft-page-filter-list-item',
  imports: [
    DsChipComponent,
    IconComponent,
    ItemComponent,
    ItemLeadingComponent,
    ItemTitleComponent,
    ItemTrailingComponent,
    FilterOperatorComponent,
    SoftSlotDirective,
    SwitchComponent,
    TranslatePipe
  ],
  templateUrl: './page-filter-list-item.component.html',
  styleUrl: './page-filter-list-item.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageFilterListItemComponent<T extends object> {
  private readonly t = translator();

  item = input.required<PageFilterItem<T, any>>();
  value = input<Filter<any> | null>();
  /** exact-match active state of a toggle item (computed by the parent against the draft) */
  toggleActive = input<boolean>(false);

  clearFilter = output<PageFilterItem<T, any>>();
  activate = output<PageFilterItem<T, any>>();

  // toggle items flip their fixed value in place (via row activate) instead of opening the detail
  readonly isToggle = computed(() => !!this.item().toggle);

  // same chip text as the menu bar and the chip row: formatted single value, count, range
  readonly valueText = computed(
    () => formatFilterValueSummary(summarizeFilterValue(this.item().config, this.value() ?? null), this.t) ?? ''
  );
  readonly showsOperator = computed(() => {
    const value = this.value();
    return !!value && value.type !== 'conditions' && showsOperatorGlyph(value.operator);
  });

  private readonly row = viewChild.required(ItemComponent, { read: ElementRef });
  /** ring after a scripted focus - :focus-visible does not fire for those */
  readonly focused = signal(false);

  focus(): void {
    const element = this.row().nativeElement as HTMLElement;
    element.focus({ preventScroll: true });
    element.scrollIntoView?.({ block: 'nearest' });
    this.focused.set(true);
  }
}
